import type { ValidationErrors } from 'final-form';
import type { StateCreator } from 'zustand';
import type { TestGroup } from '../../types';
import { type AppStore } from '../store';

/**
 * UI Slice - Manages UI state, loading indicators, errors, and flags
 *
 * State:
 * - languageConfirmed: Whether user has confirmed language selection
 * - initialRatingLoading: Loading state for initial rating calculation
 * - consentForDocuments: User consent for receiving documents
 * - isConfirmingPurchase: Whether purchase confirmation is in progress
 * - errors: Form validation errors
 * - blockingUWIssues: Whether there are blocking underwriting issues
 * - testGroup: A/B test group assignment
 *
 * Actions:
 * - setLanguageConfirmed: Update language confirmation state
 * - setInitialRatingLoading: Update initial rating loading state
 * - setConsentForDocuments: Update document consent
 * - setIsConfirmingPurchase: Update purchase confirmation state
 * - setErrors: Update form validation errors
 * - setBlockingUWIssues: Update blocking underwriting issues flag
 * - setTestGroup: Update test group assignment
 */
export interface UISlice {
  // State
  languageConfirmed: boolean | undefined;
  initialRatingLoading: boolean;
  consentForDocuments: boolean;
  isConfirmingPurchase: boolean;
  errors: ValidationErrors | undefined;
  blockingUWIssues: boolean;
  testGroup: TestGroup | undefined;

  // Actions
  setLanguageConfirmed: (confirmed: boolean | undefined) => void;
  setInitialRatingLoading: (loading: boolean) => void;
  setConsentForDocuments: (consent: boolean) => void;
  setIsConfirmingPurchase: (confirming: boolean) => void;
  setErrors: (errors: ValidationErrors | undefined) => void;
  setBlockingUWIssues: (hasIssues: boolean) => void;
  setTestGroup: (testGroup: TestGroup | undefined) => void;
}

/**
 * Determines initial rating loading state based on authentication and calculator input
 *
 * @returns true if authenticated customer with calculator input (need to load initial rating)
 */
export function getInitialRatingLoadingState(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const initialState = window.__RHVKLSO002_INITIAL_STATE;
  if (!initialState) {
    return false;
  }

  return !!(initialState.authenticatedCustomer && initialState.calculatorInput);
}

/**
 * Get initial state for UI slice
 * @returns Initial UI state with default values
 */
export const getInitialUIState = () => ({
  languageConfirmed: undefined as boolean | undefined,
  initialRatingLoading: getInitialRatingLoadingState(),
  consentForDocuments: false,
  isConfirmingPurchase: false,
  errors: undefined,
  blockingUWIssues: false,
  testGroup: undefined,
});

/**
 * Creates the UI slice
 */
export const createUISlice: StateCreator<
  AppStore,
  [['zustand/devtools', never]],
  [],
  UISlice
> = (set) => ({
  // Initial state
  ...getInitialUIState(),

  // Actions
  setLanguageConfirmed: (confirmed) => {
    set(
      { languageConfirmed: confirmed },
      false,
      'uiSlice/setLanguageConfirmed'
    );
  },

  setInitialRatingLoading: (loading) => {
    set(
      { initialRatingLoading: loading },
      false,
      'uiSlice/setInitialRatingLoading'
    );
  },

  setConsentForDocuments: (consent) => {
    set(
      { consentForDocuments: consent },
      false,
      'uiSlice/setConsentForDocuments'
    );
  },

  setIsConfirmingPurchase: (confirming) => {
    set(
      { isConfirmingPurchase: confirming },
      false,
      'uiSlice/setIsConfirmingPurchase'
    );
  },

  setErrors: (errors) => {
    set({ errors }, false, 'uiSlice/setErrors');
  },

  setBlockingUWIssues: (hasIssues) => {
    set({ blockingUWIssues: hasIssues }, false, 'uiSlice/setBlockingUWIssues');
  },

  setTestGroup: (testGroup) => {
    set({ testGroup }, false, 'uiSlice/setTestGroup');
  },
});
